import { shareMessages } from "common/modals/ShareModal";
import { CircleSpinner } from "common/spinners";
import { CTA } from "common/styleguide/CTA";
import { RadioButtonGroup } from "common/styleguide/RadioGroup";
import { OrderedMap } from "immutable";
import * as userActions from "lib/api/currentUser";
import fetchJSON from "lib/fetchJSON";
import { Msg } from "lib/localization";
import * as libPhone from "libphonenumber-js";
import React from "react";
import { FormattedMessage as T } from "react-intl";
import { connect } from "react-redux";
import { S2DLayout } from "../../../S2DLayout";
import styles from "./Step2.scss";

const yesNo = OrderedMap({
  no: "No - help me install it",
  yes: "Yes!",
});

window.unsetSkip = () => {
  userActions.setPreference("S2D:skipInstallPrompt", false);
  setTimeout(() => window.location.reload(), 1500);
};

export class Step2 extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      substep: "a",
      skipInstallPrompt: false,
    };
  }

  onNext = () => {
    const { appInstalled } = this.props;
    const { substep } = this.state;
    if (appInstalled || substep === "b") {
      this.props.onNext();
    } else {
      this.setState({ substep: "b" });
    }
  };

  onPrev = () => {
    const { substep } = this.state;
    const { skipInstallPrompt } = this.props;
    console.log("onPrev");
    if (substep === "b" && !skipInstallPrompt) {
      this.setState({ substep: "a" });
    } else {
      this.props.onPrev();
    }
  };

  onChangeSkip = (e) => {
    this.setState({ skipInstallPrompt: e.target.checked });
    // we explicitly don't involve redux here because this is a horrible hack
    userActions.setPreference("S2D:skipInstallPrompt", e.target.checked);
  };

  render() {
    const { title, subtitle, step, appInstalled, selectedDevice, onInstalledChange, deviceLabel, stepCount } =
      this.props;
    const { substep, skipInstallPrompt } = this.state;
    return (
      <S2DLayout
        title={title}
        subtitle={subtitle}
        step={step}
        onClickNext={this.onNext}
        onClickPrev={this.onPrev}
        stepCount={stepCount}
      >
        {substep === "a" && (
          <Step2a
            appInstalled={appInstalled}
            onChange={onInstalledChange}
            skipInstallPrompt={skipInstallPrompt}
            onChangeSkip={this.onChangeSkip}
            deviceLabel={deviceLabel}
          />
        )}
        {substep === "b" && <Step2b deviceLabel={deviceLabel} selectedDevice={selectedDevice} />}
      </S2DLayout>
    );
  }
}

const Step2a = ({ appInstalled, onChange, deviceLabel, skipInstallPrompt, onChangeSkip }) => (
  <div className={styles.Step2a}>
    <h3>
      <T
        id="s2d.mobileFlow2a1"
        defaultMessage="Do you have the Ride with GPS app installed on your {deviceLabel}?"
        values={{ deviceLabel }}
      />
    </h3>
    <div className={styles.formArea}>
      <RadioButtonGroup options={yesNo} selected={appInstalled ? "yes" : "no"} onChange={onChange} />
      {appInstalled && (
        <label>
          <input type="checkbox" checked={skipInstallPrompt} onChange={onChangeSkip} />{" "}
          <T id="s2d.mobileFlow2a2" defaultMessage="Don't ask me next time" />
        </label>
      )}
    </div>
  </div>
);

const mapStateToProps = (state) => ({
  skipInstallPrompt: state.currentUser.preferences.get("S2D:skipInstallPrompt"),
});

const mapDispatchToProps = {
  setPreference: userActions.setPreference,
};

export const Step2Container = connect(mapStateToProps, mapDispatchToProps)(Step2);

class Step2b extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      dialog: false,
      smsState: null, // possible values: null, pending, error, success
      phoneNumber: "",
    };
  }

  onSubmit = (e) => {
    e && e.preventDefault();
    const { selectedDevice } = this.props;
    const { phoneNumber } = this.state;

    const parsed = libPhone.parse(phoneNumber, { country: { default: "US" } });
    if (!parsed.phone || !libPhone.isValidNumber(parsed)) {
      this.setState({
        smsState: "validationError",
      });
      return;
    }

    const messageType = selectedDevice === "iphone" ? "install_link_ios" : "install_link_android";

    const body = {
      phone_number: libPhone.format(parsed, "International_plaintext"),
      message_type: messageType,
    };

    this.setState({ smsState: "pending" });
    fetchJSON(`/sms_messages`, body, { method: "POST" })
      .then(() => this.setState({ smsState: "success" }))
      .catch(() => this.setState({ smsState: "error" }));
  };

  dismissDialog = () => {
    this.setState({ dialog: false });
  };

  onChangePhone = (e) => {
    this.setState({ phoneNumber: e.target.value, smsState: null });
  };

  render() {
    const { smsState, phoneNumber } = this.state;
    const { selectedDevice } = this.props;

    let cta;
    switch (smsState) {
      case "pending":
        cta = (
          <CTA secondary>
            <CircleSpinner size={15} />
          </CTA>
        );
        break;
      case "success":
        cta = (
          <CTA secondary>
            <Msg m={shareMessages.linkSent} />
          </CTA>
        );
        break;
      default:
        cta = (
          <CTA secondary onClick={this.onSubmit}>
            <T id="s2d.sendMeTheLink" defaultMessage="Send me the link" />
          </CTA>
        );
    }

    return (
      <div className={styles.Step2b}>
        <h3>
          <T
            id="s2d.mobileFlow2b1"
            defaultMessage="Enter your phone number, and we will send you a link to download the Ride with GPS app."
          />
        </h3>
        <p>
          <T
            id="s2d.mobileFlow2b2"
            defaultMessage="You can also search for 'Ride with GPS' in the {store}"
            values={{
              store: selectedDevice === "iphone" ? "App Store" : "Play Store",
            }}
          />
        </p>
        <div className={styles.numberInput}>
          <form onSubmit={this.onSubmit}>
            <input type="tel" placeholder="Mobile phone number" value={phoneNumber} onChange={this.onChangePhone} />
          </form>
          {cta}
          {smsState === null && (
            <p className={styles.finePrint}>
              <T
                id="s2d.mobileFlow2b3"
                defaultMessage="We will not save your phone number. Standard messaging rates apply."
              />
            </p>
          )}
          {smsState === "validationError" && (
            <p className={styles.error}>
              <T
                id="s2d.mobileFlow2b4"
                defaultMessage="Oops - that doesn't look like a valid phone number. Please check your phone number and try again."
              />
            </p>
          )}
          {smsState === "error" && (
            <p className={styles.error}>
              <T
                id="s2d.mobileFlow2b5"
                defaultMessage="Oops - we had a problem sending your message. Please re-check your phone number and try again."
              />
            </p>
          )}
          {smsState === "success" && (
            <p>
              <T
                id="s2d.mobileFlow2b6"
                defaultMessage="Click the link in the SMS we just sent you to install the free Ride with GPS app."
              />
            </p>
          )}
        </div>
      </div>
    );
  }
}
